import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Plus, Calendar, Clock, Sparkles, Trash2, History } from 'lucide-react';
import { useVenue } from '../../contexts/VenueContext';
import { API, getAuthHeaders, getVenueParams } from '../../utils/venueAppApi';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { LoadingVideo } from '../../components/LoadingVideo';

const font = "'Inria Serif', serif";

const cardStyle = {
  backgroundColor: 'var(--app-bg)',
  borderRadius: 20,
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--premium-shadow-main)',
};

const CATEGORY_LABELS = {
  karaoke: 'Karaoke',
  'live-music': 'Live Music',
  'dj-set': 'DJ Set',
  party: 'Party',
  'greek-night': 'Greek Night',
  'wine-tasting': 'Wine Tasting',
  'cultural-event': 'Cultural Event',
  other: 'Other',
};

function formatDateRange(startISO, endISO) {
  const start = new Date(`${startISO}T00:00:00`);
  const end = new Date(`${endISO}T00:00:00`);
  const opts = { weekday: 'short', day: 'numeric', month: 'short' };
  const s = start.toLocaleDateString('en-GB', opts);
  if (startISO === endISO) return s;
  const e = end.toLocaleDateString('en-GB', opts);
  return `${s} → ${e}`;
}

function EventRow({ event, onDelete, onEdit, isPast }) {
  const title = event.custom_category || CATEGORY_LABELS[event.category] || 'Event';
  return (
    <div data-testid={`venue-event-row-${event.id}`} style={{
      ...cardStyle,
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      opacity: isPast ? 0.65 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {event.media_url ? (
          event.media_type === 'video' ? (
            <LoadingVideo
              dataTestId={`venue-event-row-video-${event.id}`}
              src={event.media_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${event.media_url}` : event.media_url}
              compact
              wrapperStyle={{ width: 56, height: 56, borderRadius: 14, flexShrink: 0 }}
            />
          ) : (
            <img
              src={event.media_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${event.media_url}` : event.media_url}
              alt={title}
              style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'cover', flexShrink: 0 }}
            />
          )
        ) : (
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'var(--accent-surface-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            color: 'var(--accent-purple)',
          }}>
            <Sparkles size={20} strokeWidth={2} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <p style={{ margin: 0, fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </p>
          {event.mini_description ? (
            <p style={{ margin: '4px 0 0', fontFamily: font, fontSize: 12.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word', lineHeight: 1.3 }}>
              {event.mini_description}
            </p>
          ) : null}
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10, fontFamily: font, fontSize: 11.5, color: 'var(--text-3)', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={11} strokeWidth={2.4} /> {formatDateRange(event.start_date, event.end_date)}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Clock size={11} strokeWidth={2.4} /> {event.start_time}–{event.end_time}
            </span>
          </div>
        </div>
      </div>
      {!isPast ? (
        <div style={{ display: 'flex', gap: 8, paddingTop: 4, borderTop: '1px solid var(--hairline)' }}>
          <button
            type="button"
            onClick={() => onEdit?.(event)}
            data-testid={`venue-event-edit-${event.id}`}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: 'var(--surface-2)', color: 'var(--accent-purple)',
              fontFamily: font, fontSize: 13, fontWeight: 700,
            }}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(event)}
            data-testid={`venue-event-delete-${event.id}`}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: 'rgba(229, 57, 53, 0.10)', color: '#E53935',
              fontFamily: font, fontSize: 13, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <Trash2 size={12} strokeWidth={2.4} /> Cancel event
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function VenueEventsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Same component is mounted at /venue/events and /staff-admin/events.
  // Resolve the basePath so nav links + back arrow stay inside the user's role.
  const basePath = location.pathname.startsWith('/staff-admin') ? '/staff-admin' : '/venue';
  const { activeVenueAppId } = useVenue();
  const [upcoming, setUpcoming] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!activeVenueAppId) return;
    setLoading(true);
    try {
      const [u, h] = await Promise.all([
        axios.get(`${API}/venue/events`, {
          headers: getAuthHeaders(),
          params: { ...getVenueParams(activeVenueAppId), scope: 'upcoming' },
        }),
        axios.get(`${API}/venue/events`, {
          headers: getAuthHeaders(),
          params: { ...getVenueParams(activeVenueAppId), scope: 'history' },
        }),
      ]);
      setUpcoming(u.data?.events || []);
      setHistory(h.data?.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load events');
    } finally {
      setLoading(false);
    }
  }, [activeVenueAppId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/venue/events/${confirmDelete.id}`, {
        headers: getAuthHeaders(),
        params: getVenueParams(activeVenueAppId),
      });
      toast.success('Event removed');
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove event');
    }
  };

  const list = tab === 'upcoming' ? upcoming : history;
  const listEmpty = list.length === 0;

  const counts = useMemo(() => ({ upcoming: upcoming.length, history: history.length }), [upcoming.length, history.length]);

  return (
    <div className="app-shell" data-testid="venue-events-page" style={{
      minHeight: '100vh',
      background: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      paddingBottom: 40,
    }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 0' }}>
        <button
          onClick={() => navigate(`${basePath}/scan`)}
          data-testid="venue-events-back"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          <ChevronLeft size={30} color="var(--text)" strokeWidth={2} />
        </button>
        <div style={{ width: 30 }} />
      </div>

      <div style={{ marginTop: 6 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-events-shared-logo" />
      </div>

      <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8, marginBottom: 6 }}>
        Events
      </h2>
      <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', margin: 0, padding: '0 28px', textAlign: 'center', maxWidth: 360 }}>
        Promote what's happening at your venue this week — karaoke, parties, DJ nights and more.
      </p>

      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 18 }}>
        <button
          type="button"
          data-testid="venue-events-create-btn"
          onClick={() => navigate(`${basePath}/events/new`)}
          style={{
            width: '100%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '14px 18px', borderRadius: 18, border: 'none', cursor: 'pointer',
            background: 'var(--accent-purple)',
            color: '#FFFFFF', fontFamily: font, fontSize: 15.5, fontWeight: 700,
            boxShadow: 'var(--premium-shadow-main)',
          }}
        >
          <Plus size={17} strokeWidth={2.4} /> New event
        </button>
      </div>

      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 22, display: 'flex', gap: 8 }}>
        {[
          { key: 'upcoming', label: 'Upcoming', icon: Calendar, count: counts.upcoming },
          { key: 'history', label: 'History', icon: History, count: counts.history },
        ].map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`venue-events-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              style={{
                flex: 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '10px 12px', borderRadius: 14, border: 'none', cursor: 'pointer',
                background: active ? 'var(--accent-surface)' : 'var(--surface)',
                color: active ? 'var(--accent-purple)' : 'var(--text-3)',
                fontFamily: font, fontSize: 13.5, fontWeight: 700,
                boxShadow: active ? 'var(--premium-shadow-nested)' : 'none',
              }}
            >
              <Icon size={13} strokeWidth={2.4} /> {t.label}
              <span style={{ fontSize: 11, opacity: 0.7 }}>· {t.count}</span>
            </button>
          );
        })}
      </div>

      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 14, display: 'grid', gap: 10 }}>
        {loading ? (
          <div data-testid="venue-events-loading" style={{ textAlign: 'center', color: 'var(--text-3)', fontFamily: font, padding: 30 }}>
            Loading…
          </div>
        ) : listEmpty ? (
          <div data-testid={`venue-events-empty-${tab}`} style={{
            ...cardStyle, padding: '26px 18px', textAlign: 'center',
            fontFamily: font, color: 'var(--text-3)', fontSize: 14,
          }}>
            {tab === 'upcoming'
              ? 'No upcoming events yet. Tap “New event” to publish one for this week.'
              : 'No past events yet.'}
          </div>
        ) : (
          list.map((ev) => (
            <EventRow
              key={ev.id}
              event={ev}
              isPast={tab === 'history'}
              onDelete={(e) => setConfirmDelete(e)}
              onEdit={(e) => navigate(`${basePath}/events/${e.id}/edit`)}
            />
          ))
        )}
      </div>

      {confirmDelete ? (
        <div
          onClick={() => setConfirmDelete(null)}
          data-testid="venue-events-delete-backdrop"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(12,10,32,0.55)',
            zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              ...cardStyle, padding: '20px 22px', width: '100%', maxWidth: 320, textAlign: 'center',
            }}
          >
            <p style={{ margin: '0 0 6px', fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
              Cancel this event?
            </p>
            <p style={{ margin: '0 0 16px', fontFamily: font, fontSize: 13, color: 'var(--text-3)' }}>
              Guests will no longer see it on the map.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                data-testid="venue-events-delete-cancel"
                onClick={() => setConfirmDelete(null)}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', background: 'var(--surface-2)', color: 'var(--accent-purple)', fontFamily: font, fontSize: 13, fontWeight: 700 }}
              >
                Keep it
              </button>
              <button
                type="button"
                data-testid="venue-events-delete-confirm"
                onClick={handleDelete}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', background: '#E53935', color: '#FFFFFF', fontFamily: font, fontSize: 13, fontWeight: 700 }}
              >
                Cancel event
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
